import React from 'react';
import {AccordionItemV2 as AccordionItemPepsico} from '@pepsico-ds/ui';
import PropTypes from 'prop-types';

/**
 * AccordionItem component renders an accordion item with various customizable properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.presentation='box-shadow'] - The presentation style of the accordion item.
 * @param {string} [props.content=''] - The content to be displayed inside the accordion item.
 * @param {boolean} [props.isDark=false] - Determines if the accordion item should use a dark theme.
 * @param {boolean} [props.disabled=false] - Determines if the accordion item is disabled.
 * @param {boolean} [props.expanded=false] - Determines if the accordion item is expanded.
 * @param {string} [props.icon=''] - The icon to be displayed in the accordion item.
 * @param {React.ReactNode} [props.avatar] - The avatar to be displayed in the accordion item.
 * @param {string} [props.label=''] - The label of the accordion item.
 * @param {string} [props.subTitle=''] - The subtitle of the accordion item.
 * @param {string} [props.imageUrl=''] - The URL of the image to be displayed in the accordion item.
 * @param {boolean} [props.showSkeleton=false] - Determines if a skeleton loader should be shown.
 * @param {number} props.index - The index of the accordion item.
 * @param {Function} props.onUpdate - The callback function to be called when the accordion item is updated.
 * @param {React.ReactNode} props.children - The children elements to be rendered inside the accordion item.
 * @param {React.ReactNode} props.rightAccessoryChild - The right accessory child element to be rendered in the accordion item.
 *
 * @returns {JSX.Element} The rendered AccordionItem component.
 */
function AccordionItem({
  presentation = 'box-shadow',
  content = '',
  isDark = false,
  disabled = false,
  expanded = false,
  icon = '',
  avatar,
  label = '',
  subTitle = '',
  imageUrl = '',
  showSkeleton = false,
  index,
  onUpdate,
  children,
  rightAccessoryChild
}) {
  return (
    <AccordionItemPepsico
      presentation={presentation}
      isDark={isDark}
      disabled={disabled}
      expanded={expanded}
      icon={icon}
      avatar={avatar}
      label={label}
      subTitle={subTitle}
      imageUrl={imageUrl}
      showSkeleton={showSkeleton}
      index={index}
      onUpdate={onUpdate}
      rightAccessoryChild={rightAccessoryChild}>
      {content || children}
    </AccordionItemPepsico>
  );
}

AccordionItem.propTypes = {
  presentation: PropTypes.string,
  content: PropTypes.string,
  isDark: PropTypes.bool,
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  icon: PropTypes.string,
  avatar: PropTypes.node,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subTitle: PropTypes.string,
  imageUrl: PropTypes.string,
  showSkeleton: PropTypes.bool,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  children: PropTypes.node,
  rightAccessoryChild: PropTypes.node
};

export default React.memo(AccordionItem);
